import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import CruiseLineOnly from "@/auth_descriptor/CruiseLineOnly"
import OperatorOnly from "@/auth_descriptor/OperatorOnly"
import { Button } from "react-bootstrap"
import { isBlank } from "@/utils"
import { useModal } from "@/helpers/useModal"
import {
  getCruiseDeparturesEditStates,
  getCallDaysPreEdit,
  getView,
  getDepartureRequestStates,
  getIsMakingRequestStates,
} from "../selectors"
import {
  updateCruiseDeparturesEditStates,
  updateDepartures,
  addDraftCruiseDeparture,
  onCancelEdit,
  removeDepartures,
  updateDeparturesRequestStates,
  approveDepartures,
  updateIsMakingRequestStates,
} from "../Slice"
import {
  RenderCruiseDeparturesColumn,
  calculateTotalMaximumCapacity,
  hasDepartureMismatch,
} from "./Helpers"
import DeparturesEditColumns from "./DeparturesEditColumns"
import ConfirmationModal from "./ConfirmationModal"

const CruiseLineDepartures = ({ index }) => {
  const view = useSelector(getView)
  const call = view.inventory.allocationTableData.callDays[index]
  const isClosedDay = call.inventory.currentAllocatingTour.closedDay !== null

  const { departures } = call.inventory.currentAllocatingTour.constraints
  const { date } = call

  const dispatch = useDispatch()

  const rowEditStates = useSelector(getCruiseDeparturesEditStates)
  const callDaysPreEdit = useSelector(getCallDaysPreEdit)
  const departureRequestStates = useSelector(getDepartureRequestStates)
  const isMakingRequestStates = useSelector(getIsMakingRequestStates)

  const [makingRequest, setMakingRequest] = useState(isMakingRequestStates || {})
  const [confirmationPrompt, setConfirmationPrompt] = useState("")
  const [confirmationHeader, setConfirmationHeader] = useState("")
  const [edit, setEdit] = useState(rowEditStates || {})
  const [departureRequests, setDepartureRequests] = useState(departureRequestStates || {})

  useEffect(() => {
    setEdit(rowEditStates)
    setDepartureRequests(departureRequestStates)
    if (isMakingRequestStates) {
      setMakingRequest(isMakingRequestStates)
    }
  }, [rowEditStates, departureRequestStates, isMakingRequestStates])

  const onApproveCruiselineDepartures = async () => {
    await dispatch(approveDepartures([call.date]))
  }

  const onSubmit = async (date) => {
    const newState = { ...edit, [date]: !edit[date] }
    setEdit(newState)
    const newRequestState = { ...departureRequests, [date]: !departureRequests[date] }
    setDepartureRequests(newState)

    const dateIndex = callDaysPreEdit.findIndex((callDay) => callDay.date === date)
    const previousDepartures =
      callDaysPreEdit[dateIndex].inventory.currentAllocatingTour.constraints.departures

    const departuresToAdd = departures.filter(
      (departure) => !previousDepartures.some((prevDeparture) => prevDeparture.id === departure.id)
    )

    const departuresToRemove = previousDepartures.filter(
      (prevDeparture) => !departures.some((departure) => departure.id === prevDeparture.id)
    )

    const commonDepartures = departures.filter((departure) =>
      previousDepartures.some((prevDeparture) => prevDeparture.id === departure.id)
    )

    const departuresToUpdate = commonDepartures.filter((commonDeparture) => {
      const prevDeparture = previousDepartures.find((prev) => prev.id === commonDeparture.id)
      if (!prevDeparture) return false
      return Object.keys(commonDeparture).some((key) => {
        if (key !== "id" && key !== "issues") {
          return prevDeparture[key] !== commonDeparture[key]
        }
      })
    })

    if (!isBlank(departuresToAdd)) {
      await dispatch(updateDepartures({ departures: departuresToAdd }))
    }

    if (!isBlank(departuresToUpdate)) {
      const departuresToUpdateFormatted = departuresToUpdate.map((departure) => {
        const { id, ...rest } = departure
        return { departureId: id, ...rest }
      })
      await dispatch(updateDepartures({ departures: departuresToUpdateFormatted }))
    }

    if (!isBlank(departuresToRemove)) {
      const departuresToRemoveFormatted = departuresToRemove.map((departure) => ({
        departureId: departure.id,
        remove: true,
      }))
      await dispatch(updateDepartures({ departures: departuresToRemoveFormatted }))
    }

    // set making request false so that row will collapse
    const newMakingRequestState = { ...makingRequest, [date]: false }
    setMakingRequest(newMakingRequestState)
    dispatch(updateIsMakingRequestStates({ isMakingRequestStates: newMakingRequestState }))
    dispatch(updateCruiseDeparturesEditStates({ editState: newState }))
    dispatch(updateDeparturesRequestStates({ requestState: newRequestState }))
  }

  const onEdit = (date, cancel = false) => {
    const newState = { ...edit, [date]: !edit[date] }
    setEdit(newState)
    dispatch(updateCruiseDeparturesEditStates({ editState: newState }))
    if (!cancel && isBlank(departures)) {
      dispatch(addDraftCruiseDeparture({ date }))
    }
    if (cancel) {
      const newRequestState = { ...departureRequests, [date]: !departureRequests[date] }
      setDepartureRequests(newRequestState)
      dispatch(updateDeparturesRequestStates({ requestState: newRequestState }))
      dispatch(onCancelEdit({ date }))

      // set making request false so that row will collapse
      const newMakingRequestState = { ...makingRequest, [date]: false }
      setMakingRequest(newMakingRequestState)
      dispatch(updateIsMakingRequestStates({ isMakingRequestStates: newMakingRequestState }))
    }
  }

  const onRemoveCallback = () => {
    dispatch(removeDepartures(departures))
  }

  const toggleConfirmationModal = useModal((props) => (
    <ConfirmationModal
      onConfirmationCallback={onRemoveCallback}
      buttonText="Revoke"
      confirmationPrompt={confirmationPrompt}
      header={confirmationHeader}
      {...props}
    />
  ))

  const toggleInfoModal = useModal((props) => (
    <ConfirmationModal
      confirmationPrompt={confirmationPrompt}
      closeButtonText="Ok"
      header={confirmationHeader}
      {...props}
    />
  ))

  const onRemove = () => {
    setConfirmationPrompt(
      isClosedDay
        ? `Are you sure you want to revoke your approval of the closure request for  ${call.date}`
        : `Are you sure you want to revoke all departures requested for ${call.date}`
    )
    setConfirmationHeader(isClosedDay ? "Revoking your approval" : "Confirm removal of requests")
    toggleConfirmationModal()
  }

  const onClickRequestInfo = () => {
    setConfirmationPrompt(
      "Your request represents the complete list of departures you want scheduled for the call day. If the operator accepts your request, the specified departures will replace any existing departures for that day."
    )
    setConfirmationHeader("Understanding Requests")
    toggleInfoModal()
  }

  const onClickApprovalInfo = () => {
    setConfirmationPrompt(
      "The approved inventory acts as the new capacity requirement. \n In revoking approval, the system will go back to using the original requirements to check inventory, generating issues if they are not met."
    )
    setConfirmationHeader("Understanding Approval")
    toggleInfoModal()
  }

  return (
    <>
      <OperatorOnly>
        {!isBlank(departures) && hasDepartureMismatch(call) && (
          <tr>
            <td />
            <td />
            <td />
            <td />
            <td>
              {edit[date]
                ? DeparturesEditColumns(date, departures, "departureTime", true)
                : !isBlank(departures) && (
                    <RenderCruiseDeparturesColumn
                      departures={departures}
                      property="departureTime"
                    />
                  )}
            </td>
            <td>
              {edit[date] ? (
                <>{DeparturesEditColumns(date, departures, "durationInMinutes", true)}</>
              ) : (
                !isBlank(departures) && (
                  <RenderCruiseDeparturesColumn
                    departures={departures}
                    property="durationInMinutes"
                  />
                )
              )}
            </td>
            <td>
              {edit[date]
                ? DeparturesEditColumns(date, departures, "minimumCapacity", true)
                : !isBlank(departures) && (
                    <RenderCruiseDeparturesColumn
                      departures={departures}
                      property="minimumCapacity"
                    />
                  )}
            </td>
            <td>
              {edit[date]
                ? DeparturesEditColumns(date, departures, "maximumCapacity", true)
                : !isBlank(departures) && (
                    <>
                      <RenderCruiseDeparturesColumn
                        departures={departures}
                        property="maximumCapacity"
                      />
                      <strong style={{ color: "#000000" }}>{`${calculateTotalMaximumCapacity(
                        departures
                      )} (Total)`}</strong>
                    </>
                  )}
            </td>
            <td />
            <td>Cruise Line Request</td>
            <td>
              {edit[date] ? (
                <>
                  <Button variant="secondary mb-1" onClick={() => onEdit(date, true)}>
                    Cancel
                  </Button>
                  <br />
                  <Button variant="secondary" onClick={() => onSubmit(date)}>
                    Submit
                  </Button>
                </>
              ) : (
                <Button className="primary" onClick={() => onApproveCruiselineDepartures()}>
                  Accept
                </Button>
              )}
            </td>
            <td />
            <td />
          </tr>
        )}
      </OperatorOnly>
      <CruiseLineOnly>
        {!isBlank(departures) && (
          <tr>
            <td />
            <td />
            <td />
            <td />
            <td>
              {edit[date]
                ? DeparturesEditColumns(date, departures, "departureTime", true)
                : !isBlank(departures) && (
                    <RenderCruiseDeparturesColumn
                      departures={departures}
                      property="departureTime"
                      closed={isClosedDay}
                    />
                  )}
            </td>
            <td>
              {edit[date] ? (
                <>{DeparturesEditColumns(date, departures, "durationInMinutes", true)}</>
              ) : (
                !isBlank(departures) && (
                  <RenderCruiseDeparturesColumn
                    departures={departures}
                    property="durationInMinutes"
                    closed={isClosedDay}
                  />
                )
              )}
            </td>
            <td>
              {edit[date]
                ? DeparturesEditColumns(date, departures, "minimumCapacity", true)
                : !isBlank(departures) && (
                    <RenderCruiseDeparturesColumn
                      departures={departures}
                      property="minimumCapacity"
                      closed={isClosedDay}
                    />
                  )}
            </td>
            <td>
              {edit[date]
                ? DeparturesEditColumns(date, departures, "maximumCapacity", true)
                : !isBlank(departures) && (
                    <>
                      <RenderCruiseDeparturesColumn
                        departures={departures}
                        property="maximumCapacity"
                        closed={isClosedDay}
                      />
                      {!isClosedDay && (
                        <strong style={{ color: "#000000" }}>{`${calculateTotalMaximumCapacity(
                          departures
                        )} (Total)`}</strong>
                      )}
                    </>
                  )}
            </td>
            <td />
            <td>
              {!hasDepartureMismatch(call) && !edit[date] ? (
                <span className="cursor-pointer me-1" onClick={onClickApprovalInfo}>
                  <i className="fas fa-info-circle" />
                </span>
              ) : (
                <span className="cursor-pointer me-1" onClick={onClickRequestInfo}>
                  <i className="fas fa-info-circle" />
                </span>
              )}
              {!hasDepartureMismatch(call) && !edit[date] ? "Approved" : "Request"}
            </td>
            <td>
              {edit[date] ? (
                <>
                  <Button variant="secondary mb-1" onClick={() => onEdit(date, true)}>
                    Cancel
                  </Button>
                  <br />
                  <Button variant="secondary" onClick={() => onSubmit(date)}>
                    Submit
                  </Button>
                </>
              ) : (
                <>
                  <Button className="primary mb-1" onClick={() => onEdit(date)}>
                    Edit
                  </Button>
                  <br />
                  <Button className="primary" onClick={() => onRemove()}>
                    Revoke
                  </Button>
                </>
              )}
            </td>
            <td />
            <td />
          </tr>
        )}
      </CruiseLineOnly>
    </>
  )
}

export default CruiseLineDepartures
